import { useState } from "react";
import { Camera, Wifi, WifiOff, AlertTriangle, CheckCircle2, Clock, MapPin } from "lucide-react";
import { SCADAHeader } from "@/components/scada/SCADAHeader";
import { SCADASectionCard } from "@/components/scada/SCADASectionCard";
import { ReadoutCard } from "@/components/scada/ReadoutCard";
import { AlarmBanner } from "@/components/scada/AlarmBanner";
import { StatusBadge } from "@/components/scada/StatusBadge";
import { SCADAActionBar } from "@/components/scada/SCADAActionBar";
import { InstrumentPhotoPanel } from "@/components/scada/InstrumentPhotoPanel";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

export default function SCADADemo() {
  const [isOnline] = useState(true);
  const [pendingSync] = useState(2);

  // Mock photos for demonstration
  const mockPhotos = [
    { url: "https://via.placeholder.com/150", instrumentName: "Water Meter", timestamp: "14:30" },
    { url: "https://via.placeholder.com/150", instrumentName: "Chlorine Gauge", timestamp: "14:32" },
    { url: "https://via.placeholder.com/150", instrumentName: "Pressure Gauge", timestamp: "14:35" },
  ];

  const handleSaveDraft = () => {
    console.log("Save Draft clicked");
  };

  const handleSubmit = () => {
    console.log("Submit clicked");
  };

  return (
    <div className="min-h-screen bg-background pb-32">
      {/* SCADA Header */}
      <SCADAHeader
        wellName="Well #14 - North Sector"
        location="Zone A, Pump Station 3"
        date="2025-01-18"
        status="draft"
        isOnline={isOnline}
        pendingSyncCount={pendingSync}
      />

      <div className="container max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Status Overview */}
        <div className="flex flex-wrap gap-4 items-center">
          <StatusBadge status="draft" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {isOnline ? (
              <>
                <Wifi className="h-4 w-4 text-green-500" />
                <span>Online</span>
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4 text-amber-500" />
                <span>Offline</span>
              </>
            )}
          </div>
          {pendingSync > 0 && (
            <div className="flex items-center gap-2 text-sm text-amber-500">
              <Clock className="h-4 w-4" />
              <span>{pendingSync} pending sync</span>
            </div>
          )}
        </div>

        {/* Alarm Banners */}
        <AlarmBanner>
          <div>
            <div className="font-semibold text-foreground mb-1">Meter Continuity Warning</div>
            <div className="text-muted-foreground">
              Meter start reading (12,450) differs from previous end reading (12,445). Expected: 12,445. Difference: +5 gallons.
            </div>
          </div>
        </AlarmBanner>

        <AlarmBanner>
          <div>
            <div className="font-semibold text-foreground mb-1">Production Threshold Alert</div>
            <div className="text-muted-foreground">
              Gallons pumped (285,400) is below normal minimum (300,000). Please verify meter readings.
            </div>
          </div>
        </AlarmBanner>

        {/* Water Production Section */}
        <SCADASectionCard title="Water Production" icon={<CheckCircle2 className="h-5 w-5" />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Meter Readings */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Meter Read Start
                </label>
                <Input
                  type="number"
                  value="12450"
                  className="h-14 text-lg bg-card border-border"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Meter Read End
                </label>
                <Input
                  type="number"
                  value="12735"
                  className="h-14 text-lg bg-card border-border"
                  readOnly
                />
              </div>
            </div>

            {/* Large Readout */}
            <ReadoutCard
              title="Total Pumped"
              value="285,400"
              unit="gallons"
              subtitle="Below normal minimum"
              className="border-l-4 border-l-amber-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                GPM (Flow Rate)
              </label>
              <Input
                type="number"
                value="450"
                className="h-12 text-base bg-card border-border"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Hours Run
              </label>
              <Input
                type="number"
                value="10.5"
                className="h-12 text-base bg-card border-border"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Operator Initial
              </label>
              <Input
                type="text"
                value="JD"
                className="h-12 text-base bg-card border-border"
                readOnly
              />
            </div>
          </div>
        </SCADASectionCard>

        {/* Chlorine / Chemicals Section */}
        <SCADASectionCard title="Chlorine / Chemicals" icon={<AlertTriangle className="h-5 w-5" />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Measured Inches (Cl₂)
                </label>
                <Input
                  type="number"
                  value="24.5"
                  className="h-12 text-base bg-card border-border"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Inches Used (Cl₂)
                </label>
                <Input
                  type="number"
                  value="2.3"
                  className="h-12 text-base bg-card border-border"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Lbs Cl₂ Applied
                </label>
                <Input
                  type="number"
                  value="18.5"
                  className="h-12 text-base bg-card border-border"
                  readOnly
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Free Residual (ppm)
                </label>
                <Input
                  type="number"
                  value="0.85"
                  className="h-12 text-base bg-card border-border"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Top-Off Cl₂
                </label>
                <Input
                  type="number"
                  value="5.0"
                  className="h-12 text-base bg-card border-border"
                  readOnly
                />
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <Checkbox id="delivery" checked={false} />
                <label
                  htmlFor="delivery"
                  className="text-sm font-medium text-foreground leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Chlorine Delivery Today
                </label>
              </div>
            </div>
          </div>
        </SCADASectionCard>

        {/* Pressures & Residuals Section */}
        <SCADASectionCard title="Pressures & Residuals" icon={<MapPin className="h-5 w-5" />}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                PSI (Pressure)
              </label>
              <Input
                type="number"
                value="65"
                className="h-12 text-base bg-card border-border"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Static Level
              </label>
              <Input
                type="number"
                value="120"
                className="h-12 text-base bg-card border-border"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Pumping Level
              </label>
              <Input
                type="number"
                value="185"
                className="h-12 text-base bg-card border-border"
                readOnly
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Tower Residual (ppm)
            </label>
            <Input
              type="number"
              value="0.92"
              className="h-12 text-base bg-card border-border"
              readOnly
            />
          </div>
        </SCADASectionCard>

        {/* Photos Section */}
        <SCADASectionCard title="Field Photos" icon={<Camera className="h-5 w-5" />}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {mockPhotos.map((photo, idx) => (
            <div key={idx} className="relative group">
              <img
                src={photo.url}
                alt={photo.instrumentName}
                className="w-full aspect-square object-cover rounded-lg border-2 border-border"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-xs p-2 rounded-b-lg">
                <div className="font-medium text-foreground">{photo.instrumentName}</div>
                <div className="text-muted-foreground">{photo.timestamp}</div>
              </div>
            </div>
          ))}
        </div>
          
          <div className="mt-4 text-sm text-muted-foreground">
            <p>Tap camera icons next to instrument fields to capture photos directly.</p>
          </div>
        </SCADASectionCard>

        {/* Remarks Section */}
        <SCADASectionCard title="Remarks & Notes">
          <Textarea
            placeholder="Enter any observations, maintenance notes, or anomalies..."
            className="min-h-[120px] bg-card border-border text-base"
            value="Pump running smoothly. Slight pressure fluctuation at 14:20, stabilized by 14:25. Chlorine tank at 40% capacity - delivery scheduled for next week."
            readOnly
          />
        </SCADASectionCard>

        {/* Additional / Custom Fields Section */}
        <SCADASectionCard title="Additional Fields">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Weather Conditions
              </label>
              <Select value="clear">
                <SelectTrigger className="h-12 bg-card border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clear">Clear</SelectItem>
                  <SelectItem value="cloudy">Cloudy</SelectItem>
                  <SelectItem value="rain">Rain</SelectItem>
                  <SelectItem value="snow">Snow</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Temperature (°F)
              </label>
              <Input
                type="number"
                value="72"
                className="h-12 text-base bg-card border-border"
                readOnly
              />
            </div>
          </div>

          <div className="mt-4 flex items-center space-x-2">
            <Checkbox id="maintenance" checked={false} />
            <label
              htmlFor="maintenance"
              className="text-sm font-medium text-foreground leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Maintenance Performed Today
            </label>
          </div>
        </SCADASectionCard>
      </div>

      {/* Sticky Bottom Action Bar */}
      <SCADAActionBar
        onSaveDraft={handleSaveDraft}
        onSubmit={handleSubmit}
        isOffline={!isOnline}
      />
    </div>
  );
}
